class AffiliateModal extends HTMLElement {
  constructor() {
    super();
    
    this.overlay = this.querySelector('.affiliate-modal__overlay');
    this.content = this.querySelector('.affiliate-modal__content');
    this.closeButton = this.querySelector('.affiliate-modal__close');
  }

  connectedCallback() {
    this.overlay.addEventListener('click', () => this.close());
    this.closeButton.addEventListener('click', () => this.close());
    
    // Close on ESC key
    document.addEventListener('keydown', this.handleEscapeKey.bind(this));
    
    // Trap focus inside modal
    this.setupFocusTrap();
    
    // Listen for clicks on elements with data-affiliate-modal-open attribute
    this.setupModalTriggers();
    
    // Check if we should auto-open the modal based on URL
    this.checkUrlAndAutoOpen();
  }

  checkUrlAndAutoOpen() {
    // Check if the URL contains affiliate form submission parameters
    const urlParams = new URLSearchParams(window.location.search);
    const formType = urlParams.get('contact[form_type]');
    const contactPosted = urlParams.get('contact_posted');
    const hash = window.location.hash;
    
    // Also check the raw URL for encoded/decoded versions
    const currentUrl = window.location.href;
    
    // Check for form errors (with form_type parameter)
    const isFormError = formType === 'Affiliate Application' || 
                       currentUrl.includes('Affiliate+Application') || 
                       currentUrl.includes('Affiliate%2BApplication');
    
    // Check for successful submission (contact_posted=true and form ID hash)
    const isSuccess = contactPosted === 'true' && hash === '#ContactForm__Affiliate';
    
    if (isFormError || isSuccess) {
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        this.open();
      }, 300);
    }
  }

  setupModalTriggers() {
    // Set up click handlers for all elements with data-affiliate-modal-open
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-affiliate-modal-open]');
      if (trigger) {
        // Get the href value (e.g., "#affiliate-modal")
        const href = trigger.getAttribute('href');
        if (href && href.startsWith('#')) {
          const modalId = href.substring(1); // Remove the # from #modalID
          if (modalId === this.id) {
            event.preventDefault();
            this.open();
          }
        }
      }
    });
  }

  disconnectedCallback() {
    this.toggleBodyScroll(true);
    document.removeEventListener('keydown', this.handleEscapeKey.bind(this));
  }

  open() {
    // Show the modal first
    this.style.display = 'block';
    this.setAttribute('aria-hidden', 'false');
    this.toggleBodyScroll(false);
    
    // Trigger transition by adding class after display change
    setTimeout(() => {
      this.classList.add('is-open');
    }, 10);
    
    // Focus first focusable element
    const firstInput = this.content.querySelector('input, textarea, select, button');
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 400);
    }
  }

  close() {
    // Remove class to trigger closing transition
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    
    // Wait for transition to complete before hiding
    setTimeout(() => {
      this.style.display = 'none';
      this.toggleBodyScroll(true);
    }, 300);
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      this.close();
    }
  }

  toggleBodyScroll(enable) {
    if (enable) {
      document.body.style.overflow = '';
    } else {
      document.body.style.overflow = 'hidden';
    }
  }

  setupFocusTrap() {
    const focusableElements = this.content.querySelectorAll(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    );
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    this.content.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable.focus();
          e.preventDefault();
        }
      }
    });
  }
}

customElements.define('affiliate-modal', AffiliateModal);
